/* eslint-disable */
'use client';

import { loadAxonScripts } from './axonLoader'; // Ensure this path is correct
import { Cart, LineItem } from '../../../interface/checkoutInterface';
import { RawEvent } from '../../../interface/eventInterface';
import { Tracker } from '../trackers';

declare var axon: any;

export class AxonTracker implements Tracker {
  private pixelIDs: string[];

  constructor(pixelIDs: string[]) {
    console.log('AxonTracker constructor', pixelIDs);

    if (pixelIDs.length === 0) {
      console.error(
        '[AxonTracker] Constructor: No pixel IDs provided. Throw error.',
      );
      throw new Error('AxonTracker requires at least one pixel ID.');
    }
    this.pixelIDs = pixelIDs;
  }

  Init(): void {
    loadAxonScripts(this.pixelIDs[0]); // Load scripts with the first pixel ID as eventKey
    this.initializePixels();
  }

  private initializePixels(): void {
    this.pixelIDs.forEach(pixelID => {
      axon('init', pixelID);
      axon('track', 'PAGE_VIEW', { pixel_id: pixelID });
    });
  }

  TrackEvent(event: RawEvent): void {
    this.pixelIDs.forEach(pixelID => {
      try {
        const cart: Cart | undefined = event?.cart;

        const initParams: Record<string, string> = {};
        if (cart?.Email) {
          initParams.user_email = cart.Email;
        }
        if (cart?.PhoneNumber) {
          initParams.user_phone_number = cart.PhoneNumber;
        }

        axon('init', pixelID, initParams);

        // Track the event
        switch (event.eventName) {
          case 'Purchase':
            if (cart) {
              this.handlePurchase(cart, event.orderId || '');
            }
            break;
          case 'InitiateCheckout':
            if (cart) {
              this.handleBeginCheckout(cart);
            }
            break;
          default:
            axon('track', event.eventName, { pixel_id: pixelID });
            break;
        }
      } catch (error) {
        console.error('Error tracking event:', error);
      }
    });
  }

  private handleBeginCheckout(cart: Cart): void {
    const items = cart.LineItems.map((item: LineItem) => ({
      item_variant_id: item.VariantID,
      item_id: item.ProductID,
      item_category_id: '166',
      item_name: item.Name,
      quantity: item.Quantity,
      price: parseFloat(item.UnitPrice || item.TotalPrice || '0').toFixed(2), // Ensure price is properly formatted
      image_url: item.ImageURL,
    }));

    const totalValue = items.reduce(
      (acc, item) => acc + parseFloat(item.price) * item.quantity,
      0,
    );

    const payload = {
      currency: 'USD',
      items,
      value: parseFloat(totalValue.toFixed(2)), // Round total value to two decimals
    };

    axon('track', 'begin_checkout', payload);
  }

  private handlePurchase(cart: Cart, orderId: string): void {
    const items = cart.LineItems.map((item: LineItem) => ({
      item_id: item.ProductID, // Required: Unique identifier for the item
      item_name: item.Name, // Required: Name of the item
      price: parseFloat(item.UnitPrice || item.TotalPrice || '0').toFixed(2), // Ensure price is properly formatted
      quantity: item.Quantity, // Required: Quantity of the item
      image_url: item.ImageURL,
      item_variant_id: item.VariantID,
    }));

    const totalValue = items.reduce(
      (acc, item) => acc + parseFloat(item.price) * item.quantity,
      0,
    );

    const payload = {
      currency: 'USD', // Required: ISO 4217 currency code
      items, // Required: Items array
      transaction_id: orderId, // Required: Unique identifier for the transaction
      value: parseFloat(totalValue.toFixed(2)), // Round total value to two decimals
      shipping: 0,
      tax: 0,
      user_id: cart.Email || '', // Provide a fallback if Email is undefined
    };

    this.pixelIDs.forEach(pixelID => {
      axon('track', 'purchase', { ...payload, pixel_id: pixelID });
    });
  }
}
